<template>
  <CollapsableSegment :label="$t(label)">
    <template #header>
      <ToggleSwitch
        v-model="visibility"
        class="m-0"
      />
    </template>

    <!-- Gradient legend shown only when the layer is configured with gradient -->
    <div v-if="isLayerGradient" class="mb-3">
      <div class="d-flex align-items-center">

        <div
          style="flex:1; height:12px; margin:0 8px; border-radius:6px; box-shadow: inset 0 0 0 1px rgba(0,0,0,0.05)"
          :style="{ background: gradientCss }" />

      </div>

      <div class="d-flex justify-content-between small text-muted mt-1">
        <span style="flex:1; min-width:0; white-space:normal; overflow-wrap:anywhere; word-break:break-word;">
          {{ formattedMin }}
        </span>
        <span style="flex:1; min-width:0; white-space:normal; overflow-wrap:anywhere; word-break:break-word; text-align:right;">
          {{ formattedMax }}
        </span>
      </div>
    </div>

    <!-- Stepped legend shown when gradient is not configured -->
    <ul v-else class="m-0 p-0 mb-3 list-unstyled">
      <li class="d-flex my-2 mr-3 align-items-center">
        <span class="position-relative">
          <Hexagon
            style="position: absolute"
            color="#f6f6f4"
          />
          <Hexagon
            color="hsl(213, 55%, 75%)"
            :opacity="opacity"
          />
        </span>
        <span class="mx-2 flex-grow-1">0 {{ $t('to') }} {{ breakpointsReadable[0] }} {{ legendLabel }}</span>
      </li>
      <li class="d-flex my-2 mr-3 align-items-center">
        <span class="position-relative">
          <Hexagon
            style="position: absolute"
            color="#f6f6f4"
          />
          <Hexagon
            color="hsl(213, 65%, 60%)"
            :opacity="opacity"
          />
        </span>
        <span class="mx-2 flex-grow-1">> {{ breakpointsReadable[0] }} {{ $t('to') }} {{ breakpointsReadable[1] }} {{ legendLabel }}</span>
      </li>
      <li class="d-flex my-2 mr-3 align-items-center">
        <span class="position-relative">
          <Hexagon
            style="position: absolute"
            color="#f6f6f4"
          />
          <Hexagon
            color="hsl(213, 80%, 50%)"
            :opacity="opacity"
          />
        </span>
        <span class="mx-2 flex-grow-1">> {{ breakpointsReadable[1] }} {{ $t('to') }} {{ breakpointsReadable[2] }} {{ legendLabel }}</span>
      </li>
      <li class="d-flex my-2 mr-3 align-items-center">
        <span class="position-relative">
          <Hexagon
            style="position: absolute"
            color="#f6f6f4"
          />
          <Hexagon
            color="hsl(213, 95%, 20%)"
            :opacity="opacity"
          />
        </span>
        <span class="mx-2 flex-grow-1">> {{ breakpointsReadable[2] }} {{ legendLabel }}</span>
      </li>
      <li class="d-flex my-2 mr-3 align-items-center">
        <span class="position-relative">
          <Hexagon
            style="position: absolute"
            color="#f6f6f4"
          />
          <Hexagon
            color="hsl(23, 100%, 42%)"
            :opacity="opacity"
          />
        </span>
        <span class="mx-2 flex-grow-1">{{ $t('selected') }}</span>
      </li>
    </ul>

    <p v-if="legendLabel === '*'">
      {{ legendDescription }}
    </p>
  </CollapsableSegment>
</template>

<script>
import CollapsableSegment from '@/components/common/CollapsableSegment'
import ToggleSwitch from '@/components/form/ToggleSwitch'
import Hexagon from '@/components/common/Hexagon'
import { mapGetters } from 'vuex'

import { numberWithDots } from '@/helpers/number'

const HEXAGON_PROGNOSE_LAYER = [
  'determinative',
  'inhabitants',
  'visitors',
  'commuters',
]

export default {
  name: 'LegendPrognose',
  components: {
    CollapsableSegment, ToggleSwitch, Hexagon,
  },
  props: {
    label: {
      type: String,
      default: 'components.sidebar.legend.forecast',
    },
  },
  data() {
    return {
      opacity: 0.7,
      legendMin: null,
      legendMax: null,
      colors: [
        'hsl(213, 50%, 80%)',
        'hsl(213, 65%, 55%)',
        'hsl(213, 80%, 35%)',
        'hsl(213, 95%, 20%)',
      ],
    }
  },
  computed: {
    ...mapGetters('config', {
      defaultBreakpoints: 'breakpoints',
    }),
    ...mapGetters('scenarios', [
      'forecast',
      'year',
    ]),
    ...mapGetters('prognose', [
      'ready',
    ]),
    ...mapGetters('deployment', [
      'DeploymentCode',
    ]),
    ...mapGetters('access', [
      'getActiveMunicipality',
    ]),
    ...mapGetters('layers', [
      'getForecastLayerDetails',
    ]),
    layer() {
      return this.getForecastLayerDetails.find(layer => layer.id === this.forecast)
    },
    legendLabel() {
      if (this.layer?.config?.unit) {
        return this.layer.config.unit
      }

      return this.isVlaamsGewest ? 'CPE\'s' : 'EV\'s'
    },
    legendDescription() {
      if (this.layer?.config?.unitDescription) {
        return this.layer.config.unitDescription
      }

      return ''
    },
    breakpoints() {
      if (! this.layer?.config?.breakpoints) {
        return this.defaultBreakpoints
      }

      return this.layer.config.breakpoints
    },
    breakpointsReadable() {
      return this.breakpoints.map(breakpoint => `${numberWithDots(breakpoint)}`)
    },
    /**
     * This BE prognose is a special presentation, which required different legend labels
     */
    isVlaamsGewest() {
      return this.DeploymentCode === 'be' && this.getActiveMunicipality === '2000'
    },
    visibility: {
      get() {
        return this.$store.getters['prognose/visibility']
      },
      set(visibility) {
        this.$store.commit('prognose/setVisibility', { visibility })
      },
    },
    forecastLayerName() {
      if (HEXAGON_PROGNOSE_LAYER.includes(this.forecast)) {
        return 'hexagons'
      }

      return this.forecast
    },
    // whether the currently selected layer has gradient mode configured
    isLayerGradient() {
      return !!this.layer?.config?.gradient
    },
    formattedMin() {
      return this.legendMin == null ? '-' : `${numberWithDots(this.legendMin)} ${this.legendLabel}`
    },
    formattedMax() {
      return this.legendMax == null ? '-' : `${numberWithDots(this.legendMax)} ${this.legendLabel}`
    },
    gradientCss() {
      const stops = this.colors
      const step = 100 / Math.max(1, stops.length - 1)
      const cssStops = stops.map((c, i) => `${c} ${Math.round(i * step)}%`).join(', ')
      return `linear-gradient(90deg, ${cssStops})`
    },
  },
  watch: {
    visibility(visibility) {
      if (this.ready && this.hasPrognoseLayer()) {
        if (this.layer?.config?.defaultHidden) {
          const opacity = visibility
            ? ['interpolate', ['linear'], ['zoom'], 10, 0, 10.2, 0.7, 13, 0.4]
            : 0
          this.$store.map.setPaintProperty(this.forecastLayerName, 'fill-opacity', opacity)
        } else {
          this.$store.map.setLayoutProperty(this.forecastLayerName, 'visibility', visibility ? 'visible' : 'none')
        }
      }
    },
    // when selected forecast/layer changes, recompute range
    forecast() {
      this.updateGradientRange()
    },
  },
  mounted() {
    if (this.ready) {
      this.initTransparencyLink()
    } else {
      this.$watch('ready', this.initTransparencyLink)
    }

    // compute initial range & listen for new data/tiles
    this.updateGradientRange()
    if (this.$store.map) {
      this.$store.map.on('sourcedata', this.onSourceData)
      this.$store.map.on('data', this.onSourceData)
    }
  },
  beforeDestroy() {
    if (this.ready) {
      this.$store.map.off('zoom', this.onZoom)
    }
    if (this.$store.map) {
      this.$store.map.off('sourcedata', this.onSourceData)
      this.$store.map.off('data', this.onSourceData)
    }
  },
  methods: {
    hasPrognoseLayer() {
      return !! this.$store.map.getLayer(this.forecastLayerName)
    },
    /**
     * The Hexagon transparency changes depending on the zoom level.
     *  The legend should change along with it
     */
    initTransparencyLink() {
      this.setHexoganOpacityFromZoom({ zoom: this.$store.map.getZoom() })
      this.$store.map.on('zoom', this.onZoom)
    },
    onZoom(event) {
      this.setHexoganOpacityFromZoom({ zoom: event.target.getZoom() })
    },
    /**
     * The opacity of the hexagons in the legend is matched with the
     * opacity of the layer, which is based on the zoom level of the map
     */
    setHexoganOpacityFromZoom({ zoom }) {
      if (zoom < 10) {
        this.opacity = 0.7
      } else if (zoom > 13) {
        this.opacity = 0.4
      } else {
        this.opacity = 0.7 - (((zoom - 10) / 3) * 0.3)
      }
    },
    onSourceData() {
      // recompute when tiles/features arrive
      this.updateGradientRange()
    },
    updateGradientRange() {
      // Only compute range when gradient is configured for the layer
      if (!this.layer || !this.$store.map || !this.isLayerGradient) {
        this.legendMin = null
        this.legendMax = null
        return
      }

      // 1) If layer provides countryMinMax and an entry exists for current DeploymentCode -> use it
      const countryMinMax = this.layer.config?.countryMinMax
      if (countryMinMax && this.DeploymentCode && countryMinMax[this.DeploymentCode]) {
        const entry = countryMinMax[this.DeploymentCode]
        const min = Number(entry.min)
        const max = Number(entry.max)
        if (Number.isFinite(min) && Number.isFinite(max) && min < max) {
          this.legendMin = min
          this.legendMax = max
          return
        }
      }

      // 2) Otherwise try to infer from features (rendered first, then source)
      const prop = this.layer.config?.availableYears
        ? `${this.layer.config.propertyWithData}${this.year || ''}`
        : this.layer.config?.propertyWithData

      let min = Infinity
      let max = -Infinity

      try {
        const rendered = this.$store.map.queryRenderedFeatures({ layers: [this.forecastLayerName] }) || []
        const features = (rendered.length > 0)
          ? rendered
          : (this.$store.map.querySourceFeatures && this.$store.map.querySourceFeatures(this.layer.id, { sourceLayer: this.layer?.config?.sourceLayer }) || [])

        features.forEach(f => {
          const v = Number(f.properties && f.properties[prop])
          if (!Number.isNaN(v)) {
            if (v < min) min = v
            if (v > max) max = v
          }
        })
      } catch (e) {
        // ignore query errors; fallback below
      }

      // 3) If feature inference failed, fall back to breakpoints/defaults
      if (!(isFinite(min) && isFinite(max) && min < max)) {
        const bps = Array.isArray(this.breakpoints) ? [...this.breakpoints].map(Number).sort((a, b) => a - b) : []
        const base = (bps.length >= 3) ? bps : [...this.defaultBreakpoints].map(Number).sort((a, b) => a - b)
        this.legendMin = 0
        this.legendMax = base[2] || 3
        return
      }

      // 4) Valid inferred range
      this.legendMin = min
      this.legendMax = max
    },
  },
}
</script>

<style scoped>
/* minimal spacing; adjust to match app styles */
.legend-gradient {
  display: flex;
  flex-direction: column;
}
</style>